/**
 * Composed goal-recipe catalog entries for the medical industry (plan §6.3 /
 * §6.5). Filled by the medical P4b copywriting task — owned exclusively by it.
 * Entries omit `previewPath` (added automatically in template-catalog.ts).
 *
 * `thumb`/`previewFixture` reuse the existing medical sub-industry assets so
 * the catalog preview is visually distinct for each composed goal.
 *
 * Copy discipline (plan §3.6 / task spec): owner/院長 vocabulary, no
 * marketing jargon. 医療広告ガイドライン applies throughout — no outcome/
 * effect guarantees, no before/after or "地域No.1"-style superiority claims,
 * no testimonials. Copy never promises "患者が増える" outright; it promises
 * "見つけてもらいやすくなる/伝わりやすくなる" (a discoverability/comprehension
 * improvement, not a booking-volume guarantee). `features`/`concerns.answer`
 * only reference what the composed medical templates actually generate
 * (`adapters/medical.ts`): かんたん更新、「サイトを公開」ボタン、診療時間・
 * アクセスの構造化表示、医師紹介、そのまま使える初期文言、高速表示、独自ドメ
 * イン（プランによる）。Medical composes no Payload form at all (D-01 in
 * `adapters/medical.ts`), so copy never promises a 予約フォーム／オンライン
 * 診療／問診票 — CTAs are described as 電話 + 外部予約サイト導線 only.
 */
import type { TemplateEntryDraft } from '../template-catalog';

const MEDICAL_SUB_INDUSTRIES = [
  '一般内科・総合',
  '歯科',
  '小児科',
  '皮膚科',
  '整骨院・接骨院',
  '眼科',
] as const;

export const COMPOSED_MEDICAL_TEMPLATES: readonly TemplateEntryDraft[] = [
  {
    slug: 'medical-booking',
    theme: 'medical',
    displayName: '予約につながるクリニックサイト',
    category: 'クリニック・医療',
    description:
      'ホーム上部と下部の2箇所に電話・アクセス導線を置き、診療科目の紹介・医師紹介・診療時間をその間に並べる構成。診療案内・アクセスの専用ページも備え、初めての方が「電話しよう」と思うまでを迷わせずに進めます。',
    thumb: '/lp/template-medical-dental.webp',
    previewFixture: 'dental-demo',
    fit: '電話でのお問合せ・来院を増やしたいクリニック、初診の導線を分かりやすくしたい医院',
    features: [
      'ホーム上部・下部の二重電話導線',
      '診療科目の紹介＋診療案内専用ページ',
      '医師紹介',
      '診療時間・アクセスの専用ページ',
      '独自ドメインでの公開（プランによる）',
    ],
    goal: 'booking',
    industry: 'medical',
    subIndustries: [...MEDICAL_SUB_INDUSTRIES],
    outcomeStory:
      '公開したその日から、サイトを見た方が迷わずお電話いただけるようになります。ホームの一番上に電話番号を置き、診療科目や医師紹介を読み終えた下にももう一度お問合せ先を用意しているので、「あとで電話しよう」と離脱される方を減らせます。診療時間・アクセスは専用ページでも確認できるので、初めての方でも来院までの流れが伝わりやすくなります。外部の予約サイトをお使いの場合は、そちらへの導線も併設できます。',
    concerns: [
      {
        worry: '写真や紹介文がまだ用意できていない',
        answer:
          'そのまま使える初期文言と診療科目の構成があらかじめ入っています。医師紹介や写真は準備でき次第、差し替えていただけます。',
      },
      {
        worry: '電話が来ても対応しきれるか不安',
        answer:
          '診療時間をあらかじめ構造化して表示しているので、営業時間外のお問合せを減らせます。外部予約サイトをお使いの場合はそちらへの導線も併設できます。',
      },
      {
        worry: '開業後、更新が続けられるか不安',
        answer:
          'かんたん更新の機能で、お知らせを1分程度で追加できます。入力後はダッシュボードの「サイトを公開」ボタンを押すだけで反映されます。',
      },
    ],
    businessFlow: [
      '朝、診療開始前にお知らせ欄で臨時休診・休診日の変更がないか確認する',
      '新しい診療科目や医師が加わったら、かんたん更新で紹介文を追加する',
      'ダッシュボードの「サイトを公開」ボタンを押して内容を反映する',
      '電話でのお問合せ内容を控えておき、次のお知らせ更新の参考にする',
    ],
  },
  {
    slug: 'medical-discovery',
    theme: 'medical',
    displayName: '地図と検索から見つけてもらえるクリニックサイト',
    category: 'クリニック・医療',
    description:
      'ホーム冒頭の特徴紹介の直下にアクセス・診療時間を配置し、初めて名前を知った方が「開いているか・行けそうか」をすぐ確認できる構成。お知らせ欄も備え、検索から辿り着いた方に更新され続けている医院だと伝えます。',
    thumb: '/lp/template-medical-ophthalmology.webp',
    previewFixture: 'ophthalmology-demo',
    fit: '検索や地図から新しい患者さまに見つけてほしいクリニック、開業したばかりで地域での認知を広げたい医院',
    features: [
      'アクセス・診療時間をファーストビュー直下に配置',
      '当院の方針・特徴紹介',
      '診療科目の紹介',
      'お知らせ欄',
      'アクセス専用ページ',
    ],
    goal: 'discovery',
    industry: 'medical',
    subIndustries: [...MEDICAL_SUB_INDUSTRIES],
    outcomeStory:
      '「駅からどれくらい」「今日はやっているか」——初めて医院名を検索した方が知りたいのはまずそこです。このサイトは特徴紹介のすぐ下にアクセス・診療時間を置いているので、初めての方でも迷わず「行けそうだ」と判断できます。お知らせ欄も最初から用意されているので、休診日の変更や院内の取り組みを載せておけば、検索から来た方に「今も診療している医院」だと伝わります。',
    concerns: [
      {
        worry: '文章を書くのが苦手',
        answer:
          'そのまま公開できる初期文言が入っています。まずは医院名や住所など、医院固有の情報だけ直せば形になります。',
      },
      {
        worry: '地図アプリと連携できるか分からない',
        answer:
          '独自ドメインでの公開に対応しているので（プランによる）、Googleビジネスプロフィールなど外部の地図サービスにサイトURLとして登録していただけます。',
      },
      {
        worry: '更新を続けられるか不安',
        answer:
          'お知らせは1分で1件追加できるかんたん更新に対応しています。入力後はダッシュボードの「サイトを公開」ボタンで反映されます。',
      },
    ],
    businessFlow: [
      '開業前に住所・アクセス方法・診療時間を自分の医院の情報に書き換える',
      '開業後、休診日の変更や院内のお知らせをかんたん更新で追加する',
      'ダッシュボードの「サイトを公開」ボタンで反映する',
      '地図サービスのプロフィールにサイトURLを載せて、検索からの入口を増やす',
    ],
  },
  {
    slug: 'medical-trust',
    theme: 'medical',
    displayName: '院長の人柄で選ばれるクリニックサイト',
    category: 'クリニック・医療',
    description:
      'ホームの上部で医師紹介を前面に出し、「どんな先生が診てくれるか」を先に見せてから、当院の方針やお問合せへの導線に進む構成。医師紹介専用ページとお問合せページを備え、安心して相談・来院いただける流れをつくります。',
    thumb: '/lp/template-medical.webp',
    previewFixture: 'medical-demo',
    fit: '院長の人柄や経歴で選ばれたいクリニック、地域に長く根ざしていきたい医院',
    features: [
      'ホーム上部に医師紹介',
      '医師紹介専用ページ',
      '当院の方針・特徴紹介',
      'お問合せページ（電話導線）',
    ],
    goal: 'trust',
    industry: 'medical',
    subIndustries: [...MEDICAL_SUB_INDUSTRIES],
    outcomeStory:
      '「どんな先生が診てくれるのか」が分かると、患者さまは安心してお電話いただけます。このサイトはホームの目立つ位置に医師紹介を置き、経歴や診療への考え方を伝えたうえで、お問合せへの案内につなげます。医師紹介は専用ページも用意されているので、「この先生に診てもらいたい」という安心感を、初診の前から伝えられます。',
    concerns: [
      {
        worry: '経歴や診療方針をどう見せればいいか分からない',
        answer:
          '医師紹介ページがあらかじめ用意されており、名前・肩書き・経歴・資格を入力する形式になっています。そのまま公開できる初期文言も入っています。',
      },
      {
        worry: '実績や症例写真がまだ用意できていない',
        answer:
          'この構成は院長の人柄と経歴で信頼を伝える構成です。症例写真のギャラリーは含まれないため、医療広告ガイドラインに沿った範囲で、写真がまだない状態でも公開いただけます。',
      },
      {
        worry: 'お問合せに対応しきれるか不安',
        answer:
          'お問合せページには診療時間に沿った電話導線があらかじめ入っています。配信に障害が起きた場合でも、サイトの表示自体は継続する仕組みです。',
      },
    ],
    businessFlow: [
      '開業前に医師紹介ページへ経歴・資格・診療への考え方を入力する',
      '新しい医師が加わったら、かんたん更新で医師紹介に追加する',
      'ダッシュボードの「サイトを公開」ボタンで反映する',
      'お問合せでいただいたご相談内容を院内で共有し、次回の更新に活かす',
    ],
  },
  {
    slug: 'medical-recruit',
    theme: 'medical',
    displayName: 'スタッフに選ばれるクリニックサイト',
    category: 'クリニック・医療',
    description:
      'ホームで医師紹介の直後に採用情報を配置し、「誰と働くか・どんな環境か」を求職者に先に伝える構成。採用情報専用ページで募集職種・働く環境・教育体制を紹介し、応募・お問合せへの導線につなげます。',
    thumb: '/lp/template-medical-pediatrics.webp',
    previewFixture: 'pediatrics-demo',
    fit: '看護師・医療事務などのスタッフを募集したいクリニック、採用情報をサイトで発信したい医院',
    features: [
      'ホームに採用情報セクション',
      '採用情報専用ページ（募集職種・働く環境）',
      '医師紹介',
      'お問合せページ（電話導線）',
    ],
    goal: 'recruit',
    industry: 'medical',
    subIndustries: [...MEDICAL_SUB_INDUSTRIES],
    outcomeStory:
      '求職者が知りたいのは「どんな職場か」「誰と働くか」です。このサイトはホームに医師紹介と続けて採用情報を配置し、募集職種と働く環境・教育体制を伝えたうえで、お問合せへの案内につなげます。採用情報は専用ページも用意されているので、募集要項をまとめて見てもらいながら、応募のハードルを下げられます。',
    concerns: [
      {
        worry: '募集要項をどう書けばいいか分からない',
        answer:
          '採用情報専用ページがあらかじめ用意されており、募集職種・勤務条件・教育体制を入力する形式になっています。そのまま公開できる初期文言も入っています。',
      },
      {
        worry: '採用ページを別に作る余裕がない',
        answer:
          'この構成は同じサイトの中に採用情報専用ページを備えています。追加の制作なしで、募集職種や働く環境をまとめて発信できます。',
      },
      {
        worry: '応募の問合せに対応しきれるか不安',
        answer:
          'お問合せページには診療時間に沿った電話導線があらかじめ入っています。配信に障害が起きた場合でも、サイトの表示自体は継続する仕組みです。',
      },
    ],
    businessFlow: [
      '開業前・増員のタイミングで採用情報ページへ募集職種・勤務条件を入力する',
      '募集内容が変わったら、かんたん更新で採用情報を更新する',
      'ダッシュボードの「サイトを公開」ボタンで反映する',
      '電話でのお問合せ内容を院内で共有し、面接の案内につなげる',
    ],
  },
];
